/* PEAR console — Zana across every school.
 *
 * Two things the console could not answer before. What is Zana costing in
 * total? Per-school caps stop any one school, but nothing added them up, so
 * the only way to know the platform bill was to open each school's panel in
 * turn. And is her deeper path actually on? The one failure this product has
 * already had is Zana answering fluently from her rule bank for weeks because
 * a flag was off — she does not look broken when that happens, she looks
 * mediocre, and a stress test scored her 4.5/10 before anyone worked out why.
 *
 * The kill switch lives here too, because it had no button anywhere: stopping
 * every paid call meant editing a Firestore document by hand, which is not
 * something anyone does calmly at the moment they need it.
 *
 * Engine-runs drill: mesh cron rows for LEAF + peers so platform-wide emit
 * can be verified without opening each school dock.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchZanaPlatformSpend, fetchZanaKillSwitch, setZanaKillSwitch,
  fetchEngineRuns, ZanaPlatformSpend, ZanaKillSwitch, EngineRunRow,
} from '../services/consoleApi';

function money(n: number): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return '$0';
  return `$${x.toFixed(x >= 100 ? 0 : 2)}`;
}

function ago(ms?: number): string {
  if (!ms || !Number.isFinite(ms)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ZanaPlatformPanel({ card, mono }: { card?: React.CSSProperties; mono?: string }) {
  const [spend, setSpend] = useState<ZanaPlatformSpend | null>(null);
  const [ks, setKs] = useState<ZanaKillSwitch | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [runs, setRuns] = useState<EngineRunRow[]>([]);
  const [runsByOrg, setRunsByOrg] = useState<Record<string, number>>({});
  const [runsOrg, setRunsOrg] = useState('LEAF');
  const [runsError, setRunsError] = useState<string | null>(null);

  /* Refresh used to re-fetch silently: same numbers, no spinner, no
     timestamp — "I pressed it and nothing happened" (Gabriel, 2026-08-23).
     Now it shows it is working and when it last loaded. */
  const [refreshing, setRefreshing] = useState(false);
  const [lastAt, setLastAt] = useState<Date | null>(null);
  /* 2026-09-17: the org box is read through a ref. `load` used to depend on
     `runsOrg`, and the mount effect depends on `load`, so every keystroke in
     the box re-ran the whole panel — platform spend (up to 1,000 aiUsage
     reads), the kill switch and 120 engine-run docs — and a slow response for
     "LE" could land after the one for "LEAF". Runs load on Load / Refresh. */
  const runsOrgRef = useRef(runsOrg);
  runsOrgRef.current = runsOrg;
  /** Only the newest runs request may fill the table. */
  const runsSeqRef = useRef(0);
  const loadRuns = useCallback(async (org?: string) => {
    const seq = ++runsSeqRef.current;
    const body = await fetchEngineRuns(org || undefined);
    if (seq !== runsSeqRef.current) return;
    if (!body.ok) {
      setRunsError(body.error || 'Could not load engine runs.');
      setRuns([]);
      setRunsByOrg({});
      return;
    }
    setRunsError(null);
    setRuns(body.runs || []);
    setRunsByOrg(body.byOrg || {});
  }, []);
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, k] = await Promise.all([fetchZanaPlatformSpend(), fetchZanaKillSwitch()]);
      setSpend(s);
      setKs(k);
      setLastAt(new Date());
      await loadRuns(runsOrgRef.current);
    } finally {
      setRefreshing(false);
    }
  }, [loadRuns]);

  useEffect(() => { void load(); }, [load]);

  const flip = async (off: boolean) => {
    setBusy(true);
    const row = await setZanaKillSwitch(off);
    setBusy(false);
    setConfirming(false);
    setKs(row);
    // The bar's brainOn reads the same switch, so refresh it rather than
    // letting the two halves of this panel disagree.
    void fetchZanaPlatformSpend().then(setSpend);
  };

  const stopped = !!(ks && ks.ok && ks.off);
  const brainOn = !!(spend && spend.ok && spend.brainOn);
  const unhealthy = !!(spend && spend.ok && spend.health && !spend.health.healthy);
  const top = (spend && spend.orgs) || [];
  const busiest = top[0];

  const shell: React.CSSProperties = {
    ...(card || { background: 'var(--surface, #fff)', border: '1px solid var(--border, #E9E5F3)', borderRadius: 16 }),
    padding: '20px 22px',
  };

  return (
    <div style={shell}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Zana · every school</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--t3, #5A5470)', lineHeight: 1.45, maxWidth: 560 }}>
            Total Claude spend this month across all customers, and whether her deeper
            path is reachable. Individual ceilings stay on each school's own panel.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={refreshing}
          style={{ font: 'inherit', fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, border: '1px solid var(--border, #E9E5F3)', background: 'var(--soft, #F7F5FB)', cursor: 'pointer' }}
        >
          {refreshing
            ? 'Refreshing…'
            : lastAt
              ? `Refresh · updated ${lastAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : 'Refresh'}
        </button>
      </div>

      {spend && !spend.ok && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--red, #B3261E)' }}>{spend.error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 16 }}>
        {[
          { k: 'This month', v: money((spend && spend.totalUsd) || 0) },
          { k: 'Schools', v: String((spend && spend.orgCount) || 0) },
          { k: 'Model calls', v: String((spend && spend.totalCalls) || 0) },
          { k: 'Busiest', v: busiest ? `${busiest.orgCode.split('.')[0]} · ${money(busiest.usd)}` : '—' },
        ].map((s) => (
          <div key={s.k} style={{ background: 'var(--soft, #F7F5FB)', border: '1px solid var(--hairline, #EDEAF4)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--t3, #8E86A8)' }}>{s.k}</div>
            <div style={{ fontSize: 15, fontWeight: 800, fontFamily: mono || 'ui-monospace, monospace', marginTop: 4 }}>{s.v}</div>
          </div>
        ))}
      </div>

      {spend && spend.ok && spend.totalCalls > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--t3, #5A5470)', lineHeight: 1.6 }}>
          {spend.avgTokensQuick != null && (
            <>Quick answers average <strong>{spend.avgTokensQuick.toLocaleString()}</strong> of 900 tokens
              {spend.quickCalls ? <> over {spend.quickCalls.toLocaleString()} question{spend.quickCalls === 1 ? '' : 's'}</> : null}. </>
          )}
          {spend.avgTokensDeep != null && (
            <>Deep answers average <strong>{spend.avgTokensDeep.toLocaleString()}</strong> of 1,400
              {spend.deepCalls ? <> over {spend.deepCalls.toLocaleString()} question{spend.deepCalls === 1 ? '' : 's'}</> : null}. </>
          )}
          {spend.totalCalls > spend.askCalls && spend.askCalls > 0 && (
            <>The other <strong>{(spend.totalCalls - spend.askCalls).toLocaleString()}</strong> model
              call{spend.totalCalls - spend.askCalls === 1 ? ' is a briefing' : 's are briefings'}, digests
              and takeover narration — own ceilings, not averaged here. </>
          )}
          {spend.avgTokensQuick == null && spend.avgTokensDeep == null && (
            <>Answers average <strong>{spend.avgTokensOut.toLocaleString()}</strong> tokens across both
              kinds — the per-kind split starts from the next call. </>
          )}
          {spend.truncatedCalls > 0
            ? <><strong>{spend.truncatedCalls.toLocaleString()}</strong> answer{spend.truncatedCalls === 1 ? '' : 's'} hit the ceiling and {spend.truncatedCalls === 1 ? 'was' : 'were'} retried with more room — if that number climbs, raise the ceiling. </>
            : <>No answer has hit its ceiling. </>}
          {' '}Reading your fleet costs <strong>{spend.tokensIn.toLocaleString()}</strong> tokens in
          {spend.tokensCacheRead > 0 && <> plus <strong>{spend.tokensCacheRead.toLocaleString()}</strong> cached at a tenth of the price</>}
          , which is most of the bill — the answer itself is the cheap part.
        </div>
      )}

      {spend && spend.ok && spend.truncated && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--amber, #8A5F14)' }}>
          More than 1,000 school-months this period — the total above is the first 1,000, not the whole bill.
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: stopped ? '#B3261E' : brainOn ? '#1E8E5A' : '#C9A227' }} />
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          {stopped
            ? 'Stopped by the kill switch — every school is engine-only'
            : brainOn
              ? 'Deeper reasoning is reachable'
              : 'Deeper reasoning is OFF — she is answering from the rule bank'}
        </span>
        {!stopped && !brainOn && (
          <span style={{ fontSize: 12, color: 'var(--t3, #5A5470)' }}>
            Check AI_ENABLED and AI_API_KEY on the backend.
          </span>
        )}
      </div>

      {unhealthy && spend && (
        <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: 'var(--red, #B3261E)' }}>
          Spend recording is failing for {spend.health.latchedOrgs.join(', ') || 'one or more schools'} — Zana is
          failing closed there until it recovers. This is not a budget; raising a cap will not help.
        </div>
      )}

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hairline, #EDEAF4)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>Engine runs</div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--t3, #5A5470)', lineHeight: 1.5, maxWidth: 520 }}>
              Recent mesh / sync / organ jobs. Filter by org (try LEAF and a second school) to verify the cron is platform-wide.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={runsOrg}
              onChange={(e) => setRunsOrg(e.target.value.toUpperCase())}
              placeholder="LEAF"
              style={{ font: 'inherit', fontSize: 12, fontFamily: mono || 'ui-monospace, monospace', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border, #E9E5F3)', width: 100 }}
            />
            <button
              type="button"
              onClick={() => void loadRuns(runsOrg)}
              style={{ font: 'inherit', fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, border: '1px solid var(--border, #E9E5F3)', background: 'var(--soft, #F7F5FB)', cursor: 'pointer' }}
            >
              Load
            </button>
          </div>
        </div>
        {runsError && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red, #B3261E)' }}>{runsError}</div>
        )}
        {Object.keys(runsByOrg).length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--t3, #5A5470)' }}>
            Orgs in this sample:{' '}
            {Object.entries(runsByOrg).slice(0, 8).map(([o, n]) => `${o} (${n})`).join(' · ')}
          </div>
        )}
        <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--hairline, #EDEAF4)', borderRadius: 10 }}>
          {runs.length === 0 ? (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--t3, #8E86A8)' }}>
              No engineRuns yet — wait for the next mesh-findings-sync cron tick.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, fontFamily: mono || 'ui-monospace, monospace' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--t3, #8E86A8)', background: 'var(--soft, #F7F5FB)' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 700 }}>When</th>
                  <th style={{ padding: '6px 8px', fontWeight: 700 }}>Engine</th>
                  <th style={{ padding: '6px 8px', fontWeight: 700 }}>Org</th>
                  <th style={{ padding: '6px 8px', fontWeight: 700 }}>Findings</th>
                  <th style={{ padding: '6px 8px', fontWeight: 700 }}>ms</th>
                </tr>
              </thead>
              <tbody>
                {runs.slice(0, 25).map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--hairline, #EDEAF4)' }}>
                    <td style={{ padding: '6px 8px' }}>{ago(r.at)}</td>
                    <td style={{ padding: '6px 8px' }}>{r.engine || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.orgCode || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.findingCount ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.durationMs ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hairline, #EDEAF4)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Emergency stop</div>
        <p style={{ margin: '4px 0 10px', fontSize: 12, color: 'var(--t3, #5A5470)', lineHeight: 1.5, maxWidth: 560 }}>
          Stops every paid Zana call for every school, immediately — no deploy. Facts and
          engine answers keep working, so nobody loses their numbers. Use it if spend runs
          away or an answer needs investigating.
          {ks && ks.ok && ks.updatedBy && (
            <span style={{ display: 'block', marginTop: 4, color: 'var(--t4, #8E86A8)' }}>
              Last changed by {ks.updatedBy}.
            </span>
          )}
        </p>
        {!confirming ? (
          <button
            type="button"
            disabled={busy || !ks || !ks.ok}
            onClick={() => (stopped ? void flip(false) : setConfirming(true))}
            style={{
              font: 'inherit', fontSize: 13, fontWeight: 700, padding: '9px 16px', borderRadius: 10,
              border: '1px solid transparent',
              background: stopped ? 'var(--accent, #6F4FF0)' : 'var(--red, #B3261E)',
              color: '#fff', cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Working…' : stopped ? 'Turn Zana back on' : 'Stop Zana everywhere'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Stop deeper answers for every school?</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void flip(true)}
              style={{ font: 'inherit', fontSize: 13, fontWeight: 700, padding: '9px 16px', borderRadius: 10, border: 'none', background: 'var(--red, #B3261E)', color: '#fff', cursor: 'pointer' }}
            >
              Yes, stop it
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              style={{ font: 'inherit', fontSize: 13, fontWeight: 700, padding: '9px 16px', borderRadius: 10, border: '1px solid var(--border, #E9E5F3)', background: 'var(--soft, #F7F5FB)', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
